import React, { useState } from 'react';
import { motion } from 'framer-motion';

// AppointmentPage.jsx
// Single-file React component using Tailwind CSS + Framer Motion animations.
// Drop into a Vite + React + Tailwind project. Framer Motion must be installed.

export default function AppointmentPage({ services = null }) {
  // default service list if not provided via props
  const defaultServices = services || [
    'Expert Consultation',
    'Physiotherapy Session',
    'Diagnostic Evaluation',
    'Follow-up Visit',
    'Rehabilitation Program',
  ];

  const timeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM', '02:00 PM', '02:30 PM',
    '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM',
  ];

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: defaultServices[0],
    date: '',
    time: '',
    notes: '',
  });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  function selectTime(t) {
    setForm((s) => ({ ...s, time: t }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Minimal validation
    if (!form.name || !form.email || !form.date || !form.time) {
      setStatus('error');
      return;
    }

    setStatus('sending');
    try {
      // Simulate sending — replace with actual API call
      await new Promise((r) => setTimeout(r, 900));
      setStatus('success');
      // optionally clear or keep some fields
      setForm({ name: '', email: '', phone: '', service: defaultServices[0], date: '', time: '', notes: '' });
    } catch (err) {
      setStatus('error');
    }
  }

  return (
    <div className="relative bg-gradient-to-b from-white to-cyan-50 text-slate-800 py-12 ">
      <div className="container mx-auto px-6">
        {/* HERO */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-12 mt-10">
          <div>
            <motion.h1
              initial={{ x: -24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight"
            >
              Make an Appointment
              <span className="block text-indigo-600">Easy. Fast. Confident care.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="mt-6 text-slate-600 max-w-lg"
            >
              Choose a service, pick a date & time, and we’ll confirm your appointment. If you're unsure which service suits you, select "Expert Consultation" and our team will guide you.
            </motion.p>

            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-8 flex gap-4 items-center"
            >
              <div className="rounded-lg bg-white p-4 shadow flex-1">
                <h4 className="text-sm text-slate-500">Address</h4>
                <p className="mt-1 font-medium text-slate-700">Sugandha R/A, Mirzarpool, Panchlaish, Chittagong</p>
              </div>

              <div className="rounded-lg bg-white p-4 shadow">
                <h4 className="text-sm text-slate-500">Phone</h4>
                <p className="mt-1 font-medium text-slate-700">+8801842-976655</p>
              </div>
            </motion.div>
          </div>

          {/* Animated SVG illustration */}
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md mx-auto lg:mx-0"
          >
            <svg viewBox="0 0 520 360" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <defs>
                <linearGradient id="a1" x1="0" x2="1">
                  <stop offset="0" stopColor="#6366F1" />
                  <stop offset="1" stopColor="#06B6D4" />
                </linearGradient>
              </defs>

              <motion.path
                d="M48 200 C 48 100, 160 40, 260 80 C 360 120, 460 60, 472 170 C 484 280, 360 320, 240 300 C 120 280, 48 320, 48 200 Z"
                fill="url(#a1)"
                opacity="0.14"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 6, repeat: Infinity }}
              />

              {/* calendar icon */}
              <g transform="translate(110,80)">
                <rect x="0" y="0" width="180" height="140" rx="14" fill="#fff" stroke="#e6eef6" />
                <rect x="12" y="18" width="156" height="24" rx="6" fill="#06B6D4" opacity="0.12" />
                <g transform="translate(18,56)" fill="#0f172a" fontSize="12">
                  <rect x="0" y="0" width="36" height="28" rx="6" fill="#fff" stroke="#e6eef6" />
                  <rect x="44" y="0" width="36" height="28" rx="6" fill="#fff" stroke="#e6eef6" />
                  <rect x="88" y="0" width="36" height="28" rx="6" fill="#fff" stroke="#e6eef6" />
                  <rect x="132" y="0" width="36" height="28" rx="6" fill="#fff" stroke="#e6eef6" />
                </g>

                <motion.circle cx="150" cy="32" r="6" fill="#06B6D4" animate={{ y: [0, -6, 0] }} transition={{ duration: 3, repeat: Infinity }} />
              </g>

              {/* appointment check mark */}
              <motion.g transform="translate(300,220)" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.4 }}>
                <circle cx="36" cy="36" r="36" fill="#06B6D4" opacity="0.12" />
                <path d="M20 36 L30 46 L52 26" fill="none" stroke="#06B6D4" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              </motion.g>
            </svg>
          </motion.div>
        </section>

        {/* FORM + availability */}
        <main className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <section className="lg:col-span-2 bg-white rounded-2xl p-6 shadow">
            <h3 className="text-xl font-semibold">Book your appointment</h3>
            <p className="text-sm text-slate-500 mt-1">Select a date, time and service — we will confirm shortly.</p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-600">Full name</label>
                  <input name="name" value={form.name} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-200 shadow-sm p-2 focus:ring-2 focus:ring-indigo-100" placeholder="Your full name" required />
                </div>

                <div>
                  <label className="block text-sm text-slate-600">Email</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-200 shadow-sm p-2 focus:ring-2 focus:ring-indigo-100" placeholder="you@company.com" required />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-600">Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-200 shadow-sm p-2 focus:ring-2 focus:ring-indigo-100" placeholder="+8801XXXXXXXXX" />
                </div>

                <div>
                  <label className="block text-sm text-slate-600">Service</label>
                  <select name="service" value={form.service} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-200 shadow-sm p-2 focus:ring-2 focus:ring-indigo-100">
                    {defaultServices.map((s, idx) => (
                      <option key={idx} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm text-slate-600">Date</label>
                  <input name="date" type="date" value={form.date} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-200 shadow-sm p-2 focus:ring-2 focus:ring-indigo-100" required />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm text-slate-600">Preferred time</label>
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {timeSlots.map((t) => (
                      <button type="button" key={t} onClick={() => selectTime(t)} className={`text-sm p-2 rounded-md border ${form.time === t ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700'} hover:shadow-sm transition`}>{t}</button>
                    ))}
                  </div>
                  <div className="text-xs text-slate-400 mt-2">Tip: choose a 30-minute slot. We'll call to confirm exact time.</div>
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-600">Notes (optional)</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-200 shadow-sm p-2 focus:ring-2 focus:ring-indigo-100" placeholder="Any details we should know?" rows={4}></textarea>
              </div>

              <div className="flex items-center justify-between">
                <button type="submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-indigo-600 text-white font-medium shadow hover:bg-indigo-700">
                  {status === 'sending' ? 'Booking...' : 'Request Appointment'}
                </button>

                <div className="text-sm">
                  {status === 'success' && <span className="text-green-600">Request submitted — we will contact you soon.</span>}
                  {status === 'error' && <span className="text-rose-600">Please fill required fields.</span>}
                </div>
              </div>
            </form>
          </section>

          {/* Sidebar: availability, quick info, map */}
          <aside className="bg-white rounded-2xl p-6 shadow">
            <h4 className="text-lg font-semibold">Available Today</h4>
            <p className="text-sm text-slate-500 mt-1">Appointments are available between 09:00 AM — 05:00 PM. Slots shown are typical openings.</p>

            <div className="mt-4">
              <h5 className="text-sm font-medium text-slate-600">Quick select</h5>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {['09:00 AM', '10:00 AM', '11:30 AM', '02:00 PM', '03:30 PM', '04:30 PM'].map((t) => (
                  <button key={t} type="button" onClick={() => selectTime(t)} className={`p-2 text-sm rounded ${form.time === t ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-700'} hover:shadow-sm transition`}>{t}</button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <h5 className="text-sm font-medium text-slate-600">Location</h5>
              <p className="text-sm text-slate-500">Sugandha R/A, Mirzarpool, Panchlaish, Chittagong</p>
              <div className="mt-3 h-36 rounded overflow-hidden shadow-sm">
                <iframe title="location" src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d29516.73880032355!2d91.8308553!3d22.3690162!3m2!1i1024!2i768!4f13.1!2m1!1sProcesta%20Physiotherapy%20Hospital!5e0!3m2!1sen!2sbd!4v1762371024692!5m2!1sen!2sbd" className="w-full h-full border-0" loading="lazy" />
              </div>
            </div>

            <div className="mt-6 text-xs text-slate-400">We will call to confirm your appointment. Cancellation or rescheduling: please contact us 24 hours in advance.</div>
          </aside>
        </main>
      </div>
    </div>
  );
}
